import { DealEligibility, DealOverview } from '@/components/content/deals/deal/DealSummary'
import Container from '@/components/structure/Container'
import AppStack from '@/components/content/app/AppStack'
import AppIcon from '@/components/content/app/AppIcon'
import DealClaim from './DealClaim'
import { cn, hexToRgba, getBrandColors } from '@/lib/utils'
import { checkAppBranding } from '@/lib/formatting'
import type { MenuItem } from '@/helpers/menus'
import type { App, Deal } from '@/payload-types'

type DealFooterProps = DealOverviewProps & {
  app: App
  link?: string
  passReferrerUrl?: boolean
  sponsored?: boolean
  multiple?: boolean
  menu: MenuItem[]
}

type DealOverviewProps = { deal: Deal }

export default function DealFooter({
  app,
  link,
  passReferrerUrl,
  sponsored,
  deal,
  multiple,
  menu,
}: DealFooterProps) {
  const { primaryColor, secondaryColor, tertiaryColor, backgroundColor } = getBrandColors(app)
  const { appIcon, appGlyph } = checkAppBranding(app)

  return (
    <Container reduced={true} className="relative pb-12">
      <div
        className={cn(
          'relative mx-auto flex h-auto w-full flex-col items-center justify-center gap-8 overflow-hidden rounded-2xl px-8 py-16 text-white outline outline-4 outline-gray-300 dark:outline-white/10 md:h-[200px] md:flex-row md:justify-start md:py-0 lg:px-16',
        )}
        style={{
          background: `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(primaryColor, 0.25)} 0%, ${hexToRgba(primaryColor, 0)} 80%),
        radial-gradient(70% 100% at 100% 0%, ${hexToRgba(secondaryColor, 0.25)} 0%, ${hexToRgba(secondaryColor, 0)} 80%),
        radial-gradient(100% 100% at 50% 0%, ${hexToRgba(tertiaryColor, 0.25)} 0%, ${hexToRgba(tertiaryColor, 0)} 80%, rgba(0, 0, 0, 0) 100%),
        #000`,
        }}
      >
        <AppStack
          icon={appIcon}
          glyph={appGlyph}
          name={app.name}
          multiple={multiple}
          background={backgroundColor}
          invert={backgroundColor === '#000000'}
          size="sm"
          className={multiple ? '-mt-2' : undefined}
        />

        <div className="flex flex-col items-center gap-2 md:items-start">
          <span className="text-center font-heading text-36 font-bold">
            {app.name} Deal{multiple && 's'}
          </span>
          <div className="flex flex-col items-center gap-1.5 xs:flex-row xs:gap-5">
            <DealOverview deal={deal} subMenu={menu} />
            <DealEligibility eligibility={deal.eligibility} />
          </div>
        </div>

        {link && (
          <DealClaim
            link={link}
            passReferrerUrl={passReferrerUrl}
            sponsored={sponsored}
            label="Claim"
            logo={
              <AppIcon
                icon={appIcon}
                glyph={appGlyph}
                name={app.name}
                background={backgroundColor}
                invert={backgroundColor === '#000000'}
                size={32}
              />
            }
            background={backgroundColor}
            buttonOnly={true}
            emailRequired={deal.emailIsRequired}
            desktopIsRequired={deal.desktopIsRequired}
            className="md:ml-auto"
          />
        )}
      </div>
    </Container>
  )
}
